import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useUIStore } from '@/stores/ui';

/**
 * スクロール位置の自動保存・復元フック
 *
 * スクロールイベントでリアルタイムに位置を保存し、
 * ページ遷移後に復元する。
 *
 * 使用例:
 * ```tsx
 * // 独自のrefを使わない場合
 * const containerRef = useScrollRestoration();
 *
 * // 外部refを渡す場合（usePullToReloadのref等）
 * const { ref } = usePullToReload({ ... });
 * useScrollRestoration(ref as React.RefObject<HTMLElement>);
 * ```
 */
export function useScrollRestoration(externalRef?: React.RefObject<HTMLElement>) {
  const location = useLocation();
  const { saveScrollPosition, getScrollPosition } = useUIStore();
  const internalRef = useRef<HTMLDivElement>(null);
  const containerRef = externalRef || internalRef;

  // スクロールキー: pathname + search でタブ別に保存
  const scrollKey = location.pathname + location.search;

  // スクロールイベントハンドラー（throttle付き）
  const lastSaveRef = useRef(0);
  const handleScroll = useCallback(() => {
    const now = Date.now();
    if (now - lastSaveRef.current < 100) return; // 100ms throttle
    lastSaveRef.current = now;

    if (containerRef.current) {
      saveScrollPosition(scrollKey, containerRef.current.scrollTop);
    }
  }, [scrollKey, saveScrollPosition, containerRef]);

  // スクロールイベントリスナーの登録
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, containerRef]);

  // ページ遷移時にスクロール位置を復元
  useEffect(() => {
    const savedPosition = getScrollPosition(scrollKey);

    // View Transition完了後にDOMが安定してから復元
    const timer = setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = savedPosition;
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [scrollKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return containerRef;
}
